import { useEffect, useMemo, useState } from 'react';

export type DisplayMode = 'mobile' | 'desktop';

/**
 * React hook that returns current display mode based on viewport width.
 * Mobile is defined as width <= 768px (aligned with CSS breakpoints in the project).
 */
export const useDisplayMode = (): {
  mode: DisplayMode;
  isMobile: boolean;
  isDesktop: boolean;
} => {
  const getInitial = (): DisplayMode => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return 'desktop';
    }
    return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
  };

  const [mode, setMode] = useState<DisplayMode>(getInitial);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;

    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setMode(matches ? 'mobile' : 'desktop');
    };

    // Set once on mount in case of hydration differences
    handler(mql);

    // Listen for changes (support older Safari)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler as (ev: Event) => void);
      return () => mql.removeEventListener('change', handler as (ev: Event) => void);
    } else {
      // @ts-ignore - deprecated in modern browsers but needed for older ones
      mql.addListener(handler);
      // @ts-ignore
      return () => mql.removeListener(handler);
    }
  }, []);

  const result = useMemo(() => ({
    mode,
    isMobile: mode === 'mobile',
    isDesktop: mode === 'desktop',
  }), [mode]);

  return result;
};
